/* eslint-disable react/display-name */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import DataFetchTooltip from '@shared/data-fetch-tooltip';
import './tooltip.scss';

// Base tooltip wrapper for activity logs
// Provides common structure: header, title, content, footer with link
const ActivityLogTooltip = ({
  children,
  data,
  error,
  onFetch,
  renderHeader,
  renderTitle,
  renderContent,
  renderFooter,
  linkLabel,
  onLinkClick,
  dataType
}) => {
  const getErrorStatus = useCallback(
    () => R.pathOr(null, ['response', 'status'], error),
    [error]
  );

  const renderErrorMessage = useCallback(() => {
    // A 404 for projects/events/moratorium means it no longer exists,
    // but for users it means he's either disabled or we have no permission
    // to see the full details.
    if (getErrorStatus() === 404 && dataType !== 'user') {
      return `This ${dataType} no longer exists.`;
    }
    return `You cannot view this ${dataType}'s info.`;
  }, [dataType, getErrorStatus]);

  const renderErrorPopover = useCallback(() => (
    <div styleName="tooltip-popover">
      <div styleName="body error">
        {renderErrorMessage()}
      </div>
    </div>
  ), [renderErrorMessage]);

  const renderPopover = useCallback(() => (
    <div styleName="tooltip-popover">
      <div styleName="body">
        {renderHeader && renderHeader()}
        {renderTitle && (
          <div styleName="body-title body-item">
            {renderTitle()}
          </div>
        )}
        {renderContent && renderContent()}
      </div>
      {(linkLabel || renderFooter) && <div styleName="sep" />}
      {renderFooter ? renderFooter() : (
        linkLabel && (
          <div styleName="footer">
            <div styleName="spacer" />
            <div
              role="presentation"
              onClick={onLinkClick}
              styleName="link"
            >
              {linkLabel}
            </div>
          </div>
        )
      )}
    </div>
  ), [linkLabel, onLinkClick, renderContent, renderFooter, renderHeader, renderTitle]);

  return (
    <DataFetchTooltip
      data={data}
      error={error}
      onFetch={onFetch}
      renderContent={renderPopover}
      renderError={renderErrorPopover}
    >
      {children}
    </DataFetchTooltip>
  );
};

ActivityLogTooltip.propTypes = {
  children: PropTypes.node,
  data: PropTypes.object,
  dataType: PropTypes.string,
  error: PropTypes.object,
  linkLabel: PropTypes.string,
  onFetch: PropTypes.func,
  onLinkClick: PropTypes.func,
  renderContent: PropTypes.func,
  renderFooter: PropTypes.func,
  renderHeader: PropTypes.func,
  renderTitle: PropTypes.func
};

export default ActivityLogTooltip;
